/* eslint-disable max-lines */
// import embed library initial script to extract all the translation keys also from there
import './../embed/index'
// import native translations to extract keys
import './../native/translations/translations'
import '../i18n'

import './App.scss'

import {
    EmbedContextValue,
    EmbedProvider,
} from 'components/EmbedContext'

import {
    EmbedEvent,
    EmbedEvents,
} from 'embed/types/events'

import React, {
    Suspense, useState,
} from 'react'

import ThemeProvider, {ThemeType} from 'stitches/ThemeProvider/ThemeProvider'

import Toast, {toast} from './Toast'

import {
    createTheme,
    theme as defaultTheme,
} from 'stitches/config'

import {
    darkTheme,
    embedDarkTheme,
    embedTheme,
} from 'stitches/themes'

import {
    getState, setStorageKey,
} from 'store/storage'

import {
    initialState,
    updateNinchatState,
} from 'api/slice'

import AppMain from './AppMain'
import Connection from './Connection'
import EmbedActivities from './EmbedActivitites/EmbedActivities'
import {EmbedErrorEvents} from 'embed/types/errorEvents'

import EmbedMain from './EmbedMain'
import {
    NinchatOptions,
} from 'embed/types'
import {PlatformContext} from 'components/PlatformContext'
import PromiseModal from './Modal/PromiseModal'
import {PromiseModalContext} from './Modal/PromiseModal/PromiseModalContext'
import {Provider} from 'react-redux'
import {acceptAudience} from 'api/action/acceptAudience'
import {applyEmbedLanguage} from 'embed/utils/language'
import {assignOldestTranscript} from 'api/action/assignOldestTranscript'

import {
    buildConfig,
} from 'embed/utils'

import {listeners} from 'embed/listeners'
import {navigation} from 'navigation'
import {overrideEmbedTheme} from 'stitches/overrideEmbedTheme'
import router from 'navigation/router'
import {sendToParent} from 'embed/utils/event'
import {setEnvironment} from 'appconfig'
import {setTranscriptStatus} from 'api/action/setTranscriptStatus'

import storageBackup from 'store/storage/storageBackup'
import {store} from 'store'
import {updateChannel} from 'api/action/updateChannel'

import {useTranslation} from 'react-i18next'

const Embed = (): React.ReactElement => {
    const [currentTheme, setCurrentTheme] = React.useState<ThemeType>()
    const [embedConfig, setEmbedConfig] = React.useState<EmbedContextValue>()
    const [loaded, setLoaded] = React.useState(false)
    const {t} = useTranslation()

    const [promiseModalStack, setPromiseModalStack] = useState({})

    const promiseModalValue = React.useMemo(() => ({
        promiseModalStack,
        setPromiseModalStack,
    }), [promiseModalStack])


    React.useEffect(() => {
        const {search} = window.location

        let storageDumpTimeout = 0

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_name, query] = window.name.split('?')

        // JSON parse each query param and form a NinchatOptions Object
        const params: NinchatOptions = {
            ...Object.fromEntries(Object.entries(Object.fromEntries(new URLSearchParams(query)))
                .map(([key, value]) => [
                    key,
                    JSON.parse(value),
                ])) as unknown as NinchatOptions,
            ...Object.fromEntries(new URLSearchParams(search)),
        }

        const {
            config,
            configKey,
            configUrls,
            environment,
            postConfigUrls,
            targetOrigin,
            parentUrl,
            xUserAgent,
            ssoAccessKey,
            siteSecret,
            isWebView,
            restoredStorage,
        } = params

        // parentUrl still carries the sso route in its hash; apply it to this frame's own
        // hash before navigation reads the initial route so the sso login can run.
        const ssoRouteHash = parentUrl ? new URL(parentUrl).hash : ''
        if (ssoRouteHash.includes('/x/sso/')) {
            window.history.replaceState(null, '', ssoRouteHash)
        }

        const buildConfigParams = {
            config,
            configKey,
            configUrls,
            environment,
            isWebView,
            parentUrl,
            postConfigUrls,
            ssoAccessKey,
            xUserAgent,
        }

        if (xUserAgent) {
            setEnvironment({xUserAgent})
        }

        if (siteSecret) {
            setEnvironment({siteSecret})
        }

        buildConfig(buildConfigParams, targetOrigin)
            .then(async (conf) => {
                const remember = isWebView ? false : !!conf?.remember

                // Apply the final merged site config before mounting the embed. This is a
                // display override, not a change to the user's server-side preferences.
                await applyEmbedLanguage(conf, targetOrigin)

                setEmbedConfig({
                    ...conf,
                    isWebView,
                    parentUrl,
                    remember,
                    ssoAccessKey,
                    targetOrigin,
                })

                setStorageKey(conf.sessionKey || '')

                // Restore any storage snapshot coming from the native side BEFORE reading state.
                // Otherwise getState() reads empty storage on a fresh (post-crash) load and the
                // session gets recreated with a new user_id/user_auth instead of being resumed.
                if (isWebView && restoredStorage) {
                    storageBackup.restoreStorage(remember, restoredStorage)
                }

                const stateFromStorage: any = getState()

                // only for non app embed version, app embed may share the same storage with the app
                if (conf?.variant !== 'app') {
                    if (stateFromStorage.ninchat?.user_auth && stateFromStorage.ninchat?.user_id) {
                        // for initial load we need to get the user_auth and user_id from the session/local storage and populate redux store
                        store.dispatch(updateNinchatState({...stateFromStorage.ninchat}))
                    } else {
                        // reset to initial state
                        store.dispatch(updateNinchatState({...initialState} as any))
                    }
                }

                if (conf?.variant === 'app') {
                    if (conf?.colorScheme === 'dark') {
                        setCurrentTheme(darkTheme)
                    } else if (conf?.colorScheme === 'light') {
                        setCurrentTheme(defaultTheme)
                    }
                } else if (conf.theme) {
                    const themeVariables = overrideEmbedTheme(conf?.colorScheme, conf.theme, isWebView || false)
                    const theme = createTheme('embed', themeVariables)
                    setCurrentTheme(theme)
                } else if (conf?.colorScheme === 'dark') {
                    setCurrentTheme(embedDarkTheme)
                } else {
                    setCurrentTheme(embedTheme)
                }

                listeners.start({targetOrigin}, (event: EmbedEvents) => sendToParent(event, conf.sessionKey || ''), conf)

                if (isWebView) {
                    const emitStorageDump = () => {
                        sendToParent({
                            event: EmbedEvent.EmbedStorageDump,
                            storage: storageBackup.dumpStorage(remember),
                            targetOrigin,
                        }, conf.sessionKey || '')
                    }

                    // Emit an initial dump immediately, then debounce subsequent changes so a
                    // burst of storage writes results in a single dump instead of one per write.
                    emitStorageDump()
                    storageBackup.attachStorageListener(() => {
                        if (storageDumpTimeout) {
                            clearTimeout(storageDumpTimeout)
                        }
                        storageDumpTimeout = window.setTimeout(emitStorageDump, 500)
                    })
                }

                setLoaded(true)
            })
            .catch((errorEvent: EmbedErrorEvents) => {
                setLoaded(true)
                toast.error(t('Build config error'))
                sendToParent(errorEvent, '')
            })

        const onMessage = (event: MessageEvent) => {
            // eslint-disable-next-line no-console
            // console.log('EMBED - MESSAGE FROM PARENT', event)

            if (event.origin === targetOrigin) {
                const {data} = event
                const {route} = data

                if (route && router.validate(route)) {
                    navigation.push(route.name, route.params)
                }

                if (data.event === EmbedEvent.AcceptAudience && data.queue_id) {
                    store.dispatch(acceptAudience({
                        audience_id: data.audience_id,
                        queue_id: data.queue_id,
                    }))
                }

                if (data.event === EmbedEvent.AcceptServiceRequest && data.queue_id) {
                    store.dispatch(assignOldestTranscript({queue_id: data.queue_id}))
                }

                if (data.event === EmbedEvent.CompleteServiceRequest && data.audience_id) {
                    store.dispatch(setTranscriptStatus({
                        audience_id: data.audience_id,
                        transcript_status: 'done',
                    }))
                }

                if (data.event === EmbedEvent.EndConversation && data.channel_id) {
                    store.dispatch(updateChannel({
                        channel_attrs: {closed: true},
                        channel_id: data.channel_id,
                    }))
                }
            }
        }

        window.addEventListener('message', onMessage, false)
        return () => {
            if (storageDumpTimeout) {
                clearTimeout(storageDumpTimeout)
            }
            listeners.stop()
            window.removeEventListener('message', onMessage)
        }
    }, [])

    return (
        <PlatformContext.Provider value='embed-web'>
            <PromiseModalContext.Provider value={promiseModalValue}>
                <Provider store={store}>
                    <EmbedProvider value={embedConfig}>
                        {Boolean(loaded) &&
                            <ThemeProvider theme={currentTheme}>
                                <Suspense fallback={<div className='loader' />}>
                                    <Connection />
                                    <Toast />
                                    <EmbedActivities />
                                    {embedConfig?.variant === 'app' ? <AppMain /> : <EmbedMain />}
                                    <PromiseModal />
                                </Suspense>
                            </ThemeProvider>}
                    </EmbedProvider>
                </Provider>
            </PromiseModalContext.Provider>
        </PlatformContext.Provider>
    )
}

export default Embed
